import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ImagePlus, X } from "lucide-react";
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { Textarea } from "src/@/components/ui/textarea";
import axios from "../../axios";
import { Star } from "../assets/icons/icons";

// Сколько фото можно приложить к одному отзыву (столько же проверяет бэкенд).
const MAX_PHOTOS = 6;

export const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    stars: 0,
    comment: "",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleStarClick = (selectedRating) => {
    setFormData({
      ...formData,
      stars: selectedRating,
    });
  };

  // Фото заливаются сразу при выборе — к моменту отправки уже готовы ссылки.
  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(
      0,
      MAX_PHOTOS - photos.length
    );
    if (files.length === 0) return;

    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("image", file);
        const { data } = await axios.post("/upload/review", fd);
        uploaded.push(data.url);
      } catch (err) {
        console.warn(err);
      }
    }
    setUploading(false);
    if (uploaded.length) setPhotos((prev) => [...prev, ...uploaded]);
    // сбрасываем, чтобы то же самое фото можно было выбрать снова
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.name && formData.email && formData.stars && formData.comment) {
      try {
        const today = new Date().toISOString().slice(0, 10);

        await axios.post("/reviews", { ...formData, date: today, photos });

        setSubmitted(true);
        setTimeout(() => {
          navigate("/#reviews");
        }, 2500);
      } catch (error) {
        console.error(error);
      }
    } else {
      alert("Please write all inputs");
    }
  };

  const fieldCls =
    "w-full rounded-md bg-kj-dark border border-kj-line text-kj-ink placeholder:text-kj-sub focus-visible:ring-kj-gold focus-visible:ring-offset-0 focus-visible:border-kj-gold";
  const labelCls =
    "block text-xs font-bold uppercase tracking-[0.12em] text-kj-sub";

  if (submitted) {
    return (
      <div className="w-full max-w-3xl mx-auto rounded-2xl border border-kj-line bg-kj-card p-10 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-kj-gold/15">
          <Star className="h-7 w-7 fill-kj-gold" />
        </div>
        <h3 className="text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
          {t("request.successTitle")}
        </h3>
        <p className="mt-2 text-kj-sub">{t("request.successText")}</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl mx-auto flex flex-col gap-6 rounded-2xl border border-kj-line bg-kj-card p-6 sm:p-8"
      >
        <div className="w-full flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-1/2 flex flex-col gap-1.5">
            <label htmlFor="name" className={labelCls}>
              {t("writeReview.name")}
            </label>
            <Input
              type="text"
              name="name"
              id="name"
              className={fieldCls}
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>
          <div className="w-full md:w-1/2 flex flex-col gap-1.5">
            <label htmlFor="email" className={labelCls}>
              {t("writeReview.email")}
            </label>
            <Input
              type="email"
              name="email"
              id="email"
              className={fieldCls}
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="w-full flex flex-col gap-1.5">
          <div className={labelCls}>{t("writeReview.rating")}</div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleStarClick(star)}
                className="p-1"
                aria-label={`${star}`}
              >
                <Star
                  className={`h-8 w-8 flex-shrink-0 cursor-pointer transition-colors ${
                    star <= formData.stars ? "fill-kj-gold" : "fill-kj-line"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
        <div className="w-full flex flex-col gap-1.5">
          <label htmlFor="comment" className={labelCls}>
            {t("writeReview.comment")}
          </label>
          <Textarea
            rows={4}
            name="comment"
            id="comment"
            className={fieldCls}
            value={formData.comment}
            onChange={handleInputChange}
          />
        </div>
        {/* Фото к отзыву — по желанию, максимум MAX_PHOTOS штук */}
        <div className="w-full flex flex-col gap-2">
          <div className={labelCls}>
            {t("writeReview.photos")}{" "}
            <span className="font-medium normal-case tracking-normal text-kj-sub/80">
              {t("writeReview.photosOptional")}
            </span>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotos}
            hidden
          />

          <div className="flex flex-wrap gap-3">
            {photos.map((url, i) => (
              <div
                key={`${url}-${i}`}
                className="relative h-24 w-24 rounded-lg overflow-hidden border border-kj-line"
              >
                <img
                  src={`${process.env.REACT_APP_BASE_API}${url}`}
                  alt=""
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  aria-label={t("writeReview.photoRemove")}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-kj-dark/80 text-kj-ink flex items-center justify-center hover:bg-kj-dark"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="h-24 w-24 rounded-lg border border-dashed border-kj-line text-kj-sub flex flex-col items-center justify-center gap-1 hover:border-kj-gold hover:text-kj-gold transition-colors disabled:opacity-60"
              >
                <ImagePlus className="h-5 w-5" />
                <span className="text-xs font-bold uppercase tracking-[0.08em]">
                  {uploading ? t("writeReview.photoUploading") : t("writeReview.photoAdd")}
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="w-full flex justify-end items-center">
          <Button
            type="submit"
            size="lg"
            disabled={uploading}
            className="w-full sm:w-auto bg-kj-gold text-kj-dark hover:bg-kj-gold/90 uppercase tracking-[0.08em] text-sm font-bold rounded-sm"
          >
            {t("writeReview.btnText")}
          </Button>
        </div>
      </form>
    </div>
  );
};
